import React from 'react';
import { Link } from 'react-router-dom';
import { useDashboard } from '../hooks/useDashboard';
import { useNotifications } from '../context/NotificationContext';
import { REQUEST_STATUS_LABELS } from '../services/creationRequestService';
import TicketStatusBadge from '../components/support/TicketStatusBadge';
import TicketPriorityBadge from '../components/support/TicketPriorityBadge';
import { formatRelativeDate } from '../utils/relativeTime';

// Couleurs par statut de demande (labels partagés avec l'onglet Demandes)
const REQUEST_BADGE_STYLES = {
  nouvelle: 'bg-warning/20 text-warning border border-warning/30',
  creation_en_cours: 'bg-info/20 text-info border border-info/30',
  verification: 'bg-accent/20 text-accent border border-accent/30',
  site_pret: 'bg-success/20 text-success border border-success/30'
};

const RequestStatusBadge = ({ status }) => (
  <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${REQUEST_BADGE_STYLES[status] || 'bg-base-200 text-base-content/70 border border-base-200'}`}>
    {REQUEST_STATUS_LABELS[status] || status}
  </span>
);

const getClientName = (client) =>
  `${client?.firstName || ''} ${client?.lastName || ''}`.trim() || client?.email || 'Client';

const getInitials = (name) =>
  name.replace(/[^a-zA-ZÀ-ÿ\s]/g, '').split(/\s+/).slice(0, 2).map((p) => p[0] || '').join('').toUpperCase();

const getRequestDisplayName = (request) =>
  request?.userInfo?.displayName
  || request?.businessInfo?.nomCommercial
  || request?.userInfo?.email
  || 'Client';

// Carte KPI cliquable
const KpiCard = ({ title, value, subtitle, to, accentClass }) => (
  <Link
    to={to}
    className={`bg-base-100 shadow-sm hover:shadow-md transition-all rounded-lg p-5 border-l-4 ${accentClass} block`}
  >
    <h3 className="font-bold mb-1">{title}</h3>
    <div className="text-3xl font-bold text-base-content">{value}</div>
    <p className="text-sm text-base-content/70 mt-1">{subtitle}</p>
  </Link>
);

const DashboardPage = () => {
  const { data, isLoading, isError, error } = useDashboard();
  const { adminNotifications, totalAdminUnread, markAllAdminNotificationsRead } = useNotifications();

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto p-4">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-primary mb-2">Tableau de bord</h1>
        </div>
        <div className="text-center py-20">
          <span className="loading loading-spinner loading-lg text-primary"></span>
          <p className="mt-4 text-base-content/70">Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  const d = data || {};
  const newTicketsCount = d.ticketCounts?.new || 0;

  return (
    <div className="max-w-7xl mx-auto p-4">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-primary mb-2">Tableau de bord</h1>
        <div className="text-sm breadcrumbs text-base-content/60">
          <ul>
            <li>MSD Admin</li>
            <li>Tableau de bord</li>
          </ul>
        </div>
      </div>

      {isError && (
        <div className="bg-error/10 text-error border border-error/20 rounded-lg p-4 mb-6 text-sm">
          Certaines données n'ont pas pu être chargées : {error?.message || 'erreur inconnue'}
        </div>
      )}

      {/* KPI */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
        <KpiCard
          title="Clients inscrits"
          value={d.clientsTotal || 0}
          subtitle="Tous les comptes de la plateforme"
          to="/clients"
          accentClass="border-primary"
        />
        <KpiCard
          title="Demandes en cours"
          value={d.openRequestCount || 0}
          subtitle={`${d.newRequestsCount || 0} nouvelle(s) à prendre en charge`}
          to="/creation-requests"
          accentClass="border-accent"
        />
        <KpiCard
          title="Tickets ouverts"
          value={d.openTicketCount || 0}
          subtitle={`${newTicketsCount} nouveau(x) à traiter`}
          to="/support"
          accentClass="border-warning"
        />
        <KpiCard
          title="Sites en ligne"
          value={d.sitesPublished || 0}
          subtitle={`${d.sitesTotal || 0} site(s) au total`}
          to="/clients"
          accentClass="border-success"
        />
      </div>
      {/* Notifications clients (messages demande + réponses support) */}
      <div className="bg-base-100 rounded-lg shadow-sm mb-8">
        <div className="flex items-center justify-between p-5 border-b border-base-200">
          <h2 className="text-lg font-bold text-base-content">Notifications clients</h2>
          {totalAdminUnread > 0 && (
            <button type="button" className="btn btn-sm btn-ghost" onClick={markAllAdminNotificationsRead}>
              Tout marquer lu ({totalAdminUnread})
            </button>
          )}
        </div>
        {adminNotifications.length === 0 ? (
          <p className="p-5 text-sm text-base-content/60">Aucune notification pour le moment.</p>
        ) : (
          <ul className="divide-y divide-base-200 max-h-80 overflow-y-auto">
            {adminNotifications.slice(0, 8).map((notification) => {
              const isTicket = notification.type === 'ticket_reply';
              const target = isTicket
                ? { to: '/support', label: 'Réponse client — ticket support' }
                : { to: `/creation-requests?request=${encodeURIComponent(notification.userId || '')}`, label: 'Message client — demande de création' };
              return (
                <li key={notification.id}>
                  <Link
                    to={target.to}
                    className={`flex items-start gap-3 p-4 hover:bg-base-200/60 ${notification.read ? 'opacity-60' : ''}`}
                  >
                    <span className="mt-0.5 text-lg">{isTicket ? '🎫' : '💬'}</span>
                    <span className="min-w-0 flex-1">
                      <span className={`block text-sm ${notification.read ? '' : 'font-semibold'}`}>
                        {target.label}
                      </span>
                      {notification.preview && (
                        <span className="block text-sm text-base-content/70 truncate mt-0.5">
                          “{notification.preview}”
                        </span>
                      )}
                      <span className="block text-xs text-base-content/50 mt-0.5">
                        {formatRelativeDate(notification.createdAt)}
                      </span>
                    </span>
                    {!notification.read && (
                      <span className="mt-1.5 h-2 w-2 rounded-full bg-error flex-shrink-0" />
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Files d'attente */}
      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-base-100 rounded-lg shadow-sm overflow-hidden">
          <div className="flex items-center justify-between p-5 border-b border-base-200">
            <h2 className="text-lg font-bold text-base-content">Demandes de création</h2>
            <Link to="/creation-requests" className="btn btn-sm btn-ghost">Voir tout →</Link>
          </div>
          <div className="divide-y divide-base-200">
            {(d.requestQueue || []).length === 0 ? (
              <p className="p-6 text-center text-base-content/60">Aucune demande en cours.</p>
            ) : (
              d.requestQueue.map((request) => (
                <Link
                  key={request.userId}
                  to={`/creation-requests?request=${encodeURIComponent(request.userId)}`}
                  className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 hover:bg-base-200/50 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-base-content truncate">{getRequestDisplayName(request)}</p>
                    <p className="text-xs text-base-content/60">
                      {formatRelativeDate(request.submittedAt || request.updatedAt)}
                    </p>
                  </div>
                  <RequestStatusBadge status={request.status} />
                </Link>
              ))
            )}
          </div>
        </div>

        <div className="bg-base-100 rounded-lg shadow-sm overflow-hidden">
          <div className="flex items-center justify-between p-5 border-b border-base-200">
            <h2 className="text-lg font-bold text-base-content">Tickets ouverts</h2>
            <Link to="/support" className="btn btn-sm btn-ghost">Voir tout →</Link>
          </div>
          <div className="divide-y divide-base-200">
            {(d.openTickets || []).length === 0 ? (
              <p className="p-6 text-center text-base-content/60">Aucun ticket ouvert.</p>
            ) : (
              d.openTickets.map((ticket) => (
                <Link
                  key={ticket.id}
                  to={`/support?userId=${encodeURIComponent(ticket.userId)}&ticketId=${ticket.id}`}
                  className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 hover:bg-base-200/50 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-base-content truncate">{ticket.title}</p>
                    <p className="text-xs text-base-content/60 truncate">
                      {ticket.userInfo?.displayName} · {formatRelativeDate(ticket.updatedAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <TicketStatusBadge status={ticket.status} />
                    <TicketPriorityBadge priority={ticket.priority} />
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      </div>
      {/* Derniers arrivés */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-base-100 rounded-lg shadow-sm overflow-hidden">
          <div className="flex items-center justify-between p-5 border-b border-base-200">
            <h2 className="text-lg font-bold text-base-content">Nouveaux clients</h2>
            <Link to="/clients" className="btn btn-sm btn-ghost">Voir tout →</Link>
          </div>
          <div className="divide-y divide-base-200">
            {(d.latestClients || []).length === 0 ? (
              <p className="p-6 text-center text-base-content/60">Aucun client inscrit récemment.</p>
            ) : (
              d.latestClients.map((client) => (
                <Link
                  key={client.id}
                  to={`/clients/${encodeURIComponent(client.id)}`}
                  className="flex items-center gap-3 px-5 py-3 hover:bg-base-200/50 transition-colors"
                >
                  <span className="h-9 w-9 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-bold flex-shrink-0">
                    {getInitials(getClientName(client))}
                  </span>
                  <span className="min-w-0">
                    <span className="font-medium text-base-content block truncate">{getClientName(client)}</span>
                    <span className="text-xs text-base-content/60 block truncate">
                      {client.email} · inscrit {formatRelativeDate(client.createdAt)}
                    </span>
                  </span>
                </Link>
              ))
            )}
          </div>
        </div>

        <div className="bg-base-100 rounded-lg shadow-sm overflow-hidden">
          <div className="flex items-center justify-between p-5 border-b border-base-200">
            <h2 className="text-lg font-bold text-base-content">Derniers sites publiés</h2>
            <Link to="/clients" className="btn btn-sm btn-ghost">Voir les clients →</Link>
          </div>
          <div className="divide-y divide-base-200">
            {(d.recentSites || []).length === 0 ? (
              <p className="p-6 text-center text-base-content/60">Aucun site publié récemment.</p>
            ) : (
              d.recentSites.map((site) => (
                <div
                  key={site.id}
                  className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 hover:bg-base-200/50 transition-colors"
                >
                  <Link to={`/clients/${encodeURIComponent(site.id)}`} className="min-w-0">
                    <p className="font-medium text-base-content truncate hover:underline">{site.domainName}</p>
                    <p className="text-xs text-base-content/60">publié {formatRelativeDate(site.publishedAt)}</p>
                  </Link>
                  <a
                    href={`https://${site.domainName}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-info hover:text-info-focus text-sm"
                    title="Visiter le site"
                  >
                    Ouvrir ↗
                  </a>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
